import type { ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NativeListCard } from "@/components/native/NativeListCard";
import {
  Search,
  X,
  MessageSquare,
  Calendar,
  AlertCircle,
  Wrench,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import {
  BaseSupportCase,
  STATUS_LABELS,
  PRIORITY_LABELS,
} from "@/types/support";
import { getSupportCategoryLabel } from "@/utils/supportCategoryLabel";
import { useLanguage } from "@/context/LanguageContext";
import {
  getSupportStatusColor,
  getSupportPriorityColor,
  getSupportTypeIcon,
} from "@/utils/supportUiUtils";

type SupportType = "all" | "complaint" | "service";
type StatusFilter =
  | "all"
  | "not_started"
  | "in_progress"
  | "resolved"
  | "closed";

interface AdminSupportListProps {
  L: Record<string, string>;
  selectedType: SupportType;
  setSelectedType: (t: SupportType) => void;
  selectedStatus: StatusFilter;
  handleStatusTabChange: (s: StatusFilter) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  handleSearch: () => void;
  handleClearSearch: () => void;
  isSearching: boolean;
  isLoading: boolean;
  supportCases: BaseSupportCase[];
  handleCaseClick: (c: BaseSupportCase) => void;
  supportCasesMeta: { total: number; page: number; totalPages: number };
  currentPage: number;
  handlePageChange: (p: number) => void;
  itemsPerPage: number;
  Pagination: React.ComponentType<{
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (p: number) => void;
    onItemsPerPageChange?: (n: number) => void;
  }>;
  handleItemsPerPageChange: (n: number) => void;
}

function CaseListCard({
  case_,
  L,
  onClick,
  categoryLabel,
}: {
  case_: BaseSupportCase;
  L: Record<string, string>;
  onClick: () => void;
  categoryLabel: string;
}) {
  return (
    <NativeListCard
      title={
        <span className="flex items-center gap-2 min-w-0">
          {getSupportTypeIcon(case_.type, "sm")}
          <span className="truncate">
            {case_.caseRef || case_.id}
          </span>
          {case_.hasUnread ? (
            <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
          ) : null}
        </span>
      }
      subtitle={case_.customerName}
      meta={
        <span className="flex flex-col items-end gap-0.5 text-xs">
          <span>{format(new Date(case_.created), "dd/MM/yyyy")}</span>
          <span className="text-muted-foreground">{categoryLabel}</span>
        </span>
      }
      badges={
        <>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {case_.type === "complaint"
              ? (L.complaint ?? "Reklamation")
              : (L.service ?? "Service")}
          </Badge>
          <Badge className={`text-[10px] px-1.5 py-0 ${getSupportStatusColor(case_.status)}`}>
            {STATUS_LABELS[case_.status as keyof typeof STATUS_LABELS]}
          </Badge>
          <Badge className={`text-[10px] px-1.5 py-0 ${getSupportPriorityColor(case_.priority)}`}>
            {PRIORITY_LABELS[case_.priority as keyof typeof PRIORITY_LABELS]}
          </Badge>
        </>
      }
      onClick={onClick}
    />
  );
}

export function AdminSupportPageContent(props: AdminSupportListProps) {
  const {
    L,
    selectedType,
    setSelectedType,
    selectedStatus,
    handleStatusTabChange,
    searchQuery,
    setSearchQuery,
    handleSearch,
    handleClearSearch,
    isSearching,
    isLoading,
    supportCases,
  handleCaseClick,
  supportCasesMeta,
    currentPage,
    handlePageChange,
    itemsPerPage,
    Pagination,
    handleItemsPerPageChange,
  } = props;

  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const useCompactList = isMobile || isNative;
  const labelForCategory = (category: string) =>
    getSupportCategoryLabel(category, language);

  const filteredCases = supportCases.filter(
    (case_) => selectedStatus === "all" || case_.status === selectedStatus,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={L.searchCustomer ?? "Sök kund, ID eller reg nr..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={handleSearch}
            disabled={!searchQuery.trim()}
            className="flex-1 sm:flex-none"
          >
            {L.search ?? "Sök"}
          </Button>
          {isSearching ? (
            <Button variant="outline" onClick={handleClearSearch} size="icon">
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        <Button
          variant={selectedType === "all" ? "default" : "outline"}
          size="sm"
          className="shrink-0 h-8 text-xs"
          onClick={() => setSelectedType("all")}
        >
          <MessageSquare className="h-3.5 w-3.5 mr-1" />
          {L.all ?? "Alla"}
        </Button>
        <Button
          variant={selectedType === "complaint" ? "default" : "outline"}
          size="sm"
          className="shrink-0 h-8 text-xs"
          onClick={() => setSelectedType("complaint")}
        >
          <AlertCircle className="h-3.5 w-3.5 mr-1" />
          {L.complaints ?? "Reklamationer"}
        </Button>
        <Button
          variant={selectedType === "service" ? "default" : "outline"}
          size="sm"
          className="shrink-0 h-8 text-xs"
          onClick={() => setSelectedType("service")}
        >
          <Wrench className="h-3.5 w-3.5 mr-1" />
          {L.service ?? "Service"}
        </Button>
      </div>

      <Tabs
        value={selectedStatus}
        onValueChange={(v) => handleStatusTabChange(v as StatusFilter)}
        className="space-y-3"
      >
        <TabsList className="w-full h-auto flex overflow-x-auto justify-start gap-0.5 p-1">
          {(
            [
              ["all", L.all ?? "Alla"],
              ["not_started", L.notStarted ?? "Ej påbörjad"],
              ["in_progress", L.inProgress ?? "Pågående"],
              ["resolved", L.resolved ?? "Löst"],
              ["closed", L.closed ?? "Stängd"],
            ] as const
          ).map(([value, label]) => (
            <TabsTrigger
              key={value}
              value={value}
              className="text-xs px-2.5 py-1.5 shrink-0"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {(
          ["all", "not_started", "in_progress", "resolved", "closed"] as const
        ).map((status) => (
          <TabsContent key={status} value={status} className="mt-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm">{L.loadingCases ?? "Laddar..."}</span>
              </div>
            ) : filteredCases.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-10">
                {L.noCases ?? "Inga ärenden"}
              </p>
            ) : useCompactList ? (
              <div className="space-y-2">
                {filteredCases.map((case_) => (
                  <CaseListCard
                    key={case_.id}
                    case_={case_}
                    L={L}
                    onClick={() => handleCaseClick(case_)}
                    categoryLabel={labelForCategory(case_.category)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">ID</th>
                        <th className="text-left p-2 font-medium">
                          {L.customer ?? "Kund"}
                        </th>
                        <th className="text-left p-2 font-medium hidden md:table-cell">
                          {L.type ?? "Typ"}
                        </th>
                        <th className="text-left p-2 font-medium">
                          {L.category ?? "Kategori"}
                        </th>
                        <th className="text-left p-2 font-medium">
                          {L.status ?? "Status"}
                        </th>
                        <th className="text-left p-2 font-medium hidden lg:table-cell">
                          {L.priority ?? "Prioritet"}
                        </th>
                        <th className="text-left p-2 font-medium hidden sm:table-cell">
                          {L.created ?? "Skapad"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCases.map((case_) => (
                        <tr
                          key={case_.id}
                          className="border-b cursor-pointer hover:bg-muted/40"
                          onClick={() => handleCaseClick(case_)}
                        >
                          <td className="p-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {getSupportTypeIcon(case_.type)}
                              <span className="truncate max-w-[120px]">
                                {case_.caseRef || case_.id}
                              </span>
                              {case_.hasUnread ? (
                                <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                              ) : null}
                            </div>
                          </td>
                          <td className="p-2 min-w-[140px]">
                            <div className="font-medium truncate">
                              {case_.customerName}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {case_.customerEmail}
                            </div>
                          </td>
                          <td className="p-2 hidden md:table-cell">
                            <Badge variant="outline" className="text-xs">
                              {case_.type === "complaint"
                                ? (L.complaint ?? "Reklamation")
                                : (L.service ?? "Service")}
                            </Badge>
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            {labelForCategory(case_.category)}
                          </td>
                          <td className="p-2">
                            <Badge
                              className={`text-xs ${getSupportStatusColor(case_.status)}`}
                            >
                              {
                                STATUS_LABELS[
                                  case_.status as keyof typeof STATUS_LABELS
                                ]
                              }
                            </Badge>
                          </td>
                          <td className="p-2 hidden lg:table-cell">
                            <Badge
                              className={`text-xs ${getSupportPriorityColor(case_.priority)}`}
                            >
                              {
                                PRIORITY_LABELS[
                                  case_.priority as keyof typeof PRIORITY_LABELS
                                ]
                              }
                            </Badge>
                          </td>
                          <td className="p-2 hidden sm:table-cell whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 text-xs">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(case_.created), "dd/MM/yyyy")}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {supportCasesMeta.totalPages > 1 ? (
              <div className="pt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={supportCasesMeta.totalPages}
                  totalItems={supportCasesMeta.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
